import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { User, Mail, Building, Hash, MapPin, Globe } from "lucide-react";

export interface UserProfileData {
    first_name: string;
    last_name: string;
    first_name_ar: string;
    last_name_ar: string;
    college_name: string;
    college_id: number;
    academic_role?: 'student' | 'faculty';
    address: string;
    username: string;
    email?: string;
}

interface ProfileCardProps {
    profile: UserProfileData;
    email?: string;
}

const ProfileCard = ({ profile, email }: ProfileCardProps) => {
    const initials = `${profile.first_name?.[0] || ""}${profile.last_name?.[0] || ""}`.toUpperCase();
    const displayEmail = email || profile.email;

    return (
        <Card className="overflow-hidden shadow-lg bg-card/50 backdrop-blur-sm border-primary/10">
            {/* Cover Image / Header Background */}
            <div className="h-32 bg-gradient-to-r from-primary/80 to-accent/80 relative">
                <div className="absolute -bottom-12 left-8">
                    <Avatar className="h-24 w-24 border-4 border-background shadow-md">
                        <AvatarImage src="" alt="Profile Picture" />
                        <AvatarFallback className="bg-primary/10 text-primary text-2xl font-bold">
                            {initials || <User className="h-10 w-10" />}
                        </AvatarFallback>
                    </Avatar>
                </div>
            </div>

            <div className="pt-16 pb-8 px-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Personal Info */}
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-primary border-b border-primary/20 pb-2">
                            Personal Information
                        </h2>

                        <div className="flex items-start gap-3">
                            <User className="w-5 h-5 text-muted-foreground mt-1" />
                            <div>
                                <p className="text-sm text-muted-foreground">Full Name (English)</p>
                                <p className="text-lg font-medium">
                                    {profile.first_name} {profile.last_name}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <Globe className="w-5 h-5 text-muted-foreground mt-1" />
                            <div>
                                <p className="text-sm text-muted-foreground">Full Name (Arabic)</p>
                                <p className="text-lg font-medium font-tajawal" dir="rtl">
                                    {profile.first_name_ar} {profile.last_name_ar || 'N/A'}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <Mail className="w-5 h-5 text-muted-foreground mt-1" />
                            <div>
                                <p className="text-sm text-muted-foreground">Email</p>
                                <p className="text-lg font-medium">{displayEmail || 'N/A'}</p>
                            </div>
                        </div>
                    </div>

                    {/* Academic Info */}
                    <div className="space-y-6">
                        <h2 className="text-xl font-semibold text-primary border-b border-primary/20 pb-2">
                            Academic Information
                        </h2>

                        <div className="flex items-start gap-3">
                            <Building className="w-5 h-5 text-muted-foreground mt-1" />
                            <div>
                                <p className="text-sm text-muted-foreground">College</p>
                                <p className="text-lg font-medium font-tajawal" dir="rtl">
                                    {profile.college_name || 'N/A'}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <User className="w-5 h-5 text-muted-foreground mt-1" /> {/* Using User icon for role */}
                            <div>
                                <p className="text-sm text-muted-foreground">Role</p>
                                <p className="text-lg font-medium capitalize">
                                    {profile.academic_role || 'N/A'}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <MapPin className="w-5 h-5 text-muted-foreground mt-1" />
                            <div>
                                <p className="text-sm text-muted-foreground">Address</p>
                                <p className="text-lg font-medium">
                                    {profile.address || 'N/A'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default ProfileCard;
